import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTourDetails } from './useTourDetails';
import TourMap from 'dashboard/components/TourMap';
import LocationPicker from 'dashboard/components/LocationPicker';
import VideoPlayer from 'dashboard/components/VideoPlayer';
import PostSelector from 'dashboard/components/PostSelector';
import './_TourDetails.scss';

const TourDetails: React.FC = () => {
  const {
    selectedTour,
    formData,
    handleInputChange,
    handleSubmit,
    handleDelete,
    isStepModalOpen,
    editingStep,
    stepFormData,
    handleOpenStepModal,
    handleCloseStepModal,
    handleStepInputChange,
    handleSubmitStep,
    handleDeleteStep,
    handleLocationSelect,
    categories,
    loadingCategories,
    t,
  } = useTourDetails();

  // State pour gérer l'affichage des vidéos par étape
  const [expandedVideos, setExpandedVideos] = useState<Set<number>>(new Set());

  const toggleVideo = (stepOrder: number) => {
    const newExpanded = new Set(expandedVideos);
    if (newExpanded.has(stepOrder)) {
      newExpanded.delete(stepOrder);
    } else {
      newExpanded.add(stepOrder);
    }
    setExpandedVideos(newExpanded);
  };

  if (!selectedTour) return <></>;

  return (
    <section className="content mt-3">
      <div className="row">
        {/* Formulaire principal du tour */}
        <div className="col-lg-8">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {t('dashboard.tourPage.editTitle') || 'Modifier le parcours'}
              </h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mb-4">
                    <label htmlFor="name" className="form-label">
                      {t('dashboard.tourPage.name') || 'Nom'} * (FR)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="col-md-12 mb-4">
                    <label htmlFor="name_en" className="form-label">
                      {t('dashboard.tourPage.name') || 'Nom'} (EN)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name_en"
                      name="name_en"
                      value={formData.name_en || ''}
                      onChange={handleInputChange}
                      placeholder="English name"
                    />
                  </div>

                  <div className="col-md-12 mb-4">
                    <label htmlFor="description" className="form-label">
                      {t('dashboard.tourPage.description') || 'Description'} * (FR)
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div className="col-md-12 mb-4">
                    <label htmlFor="description_en" className="form-label">
                      {t('dashboard.tourPage.description') || 'Description'} (EN)
                    </label>
                    <textarea
                      className="form-control"
                      id="description_en"
                      name="description_en"
                      value={formData.description_en || ''}
                      onChange={handleInputChange}
                      rows={5}
                      placeholder="English description"
                    />
                  </div>

                  <div className="col-md-6 mb-4">
                    <label htmlFor="categoryTour" className="form-label">
                      {t('dashboard.tourPage.category') || 'Catégorie'} *
                    </label>
                    <select
                      className="form-control"
                      id="categoryTour"
                      name="categoryTour"
                      value={formData.categoryTour}
                      onChange={handleInputChange}
                      required
                      disabled={loadingCategories}
                    >
                      <option value="">
                        {loadingCategories ? 'Chargement...' : 'Sélectionnez une catégorie'}
                      </option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-4">
                    <label htmlFor="difficulty" className="form-label">
                      {t('dashboard.tourPage.difficulty') || 'Difficulté'}
                    </label>
                    <select
                      className="form-control"
                      id="difficulty"
                      name="difficulty"
                      value={formData.difficulty}
                      onChange={handleInputChange}
                    >
                      <option value="easy">🟢 Facile</option>
                      <option value="medium">🟡 Moyen</option>
                      <option value="hard">🔴 Difficile</option>
                    </select>
                  </div>

                  <div className="col-md-4 mb-4">
                    <label htmlFor="estimatedDuration" className="form-label">
                      {t('dashboard.tourPage.duration') || 'Durée estimée (min)'}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="estimatedDuration"
                      name="estimatedDuration"
                      value={formData.estimatedDuration}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>

                  <div className="col-md-4 mb-4">
                    <label htmlFor="distance" className="form-label">
                      {t('dashboard.tourPage.distance') || 'Distance (km)'}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="distance"
                      name="distance"
                      value={formData.distance}
                      onChange={handleInputChange}
                      min="0"
                      step="0.1"
                    />
                  </div>

                  <div className="col-md-4 mb-4">
                    <label htmlFor="price" className="form-label">
                      {t('dashboard.tourPage.price') || 'Prix (€)'}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-md-4 mb-4">
                    <label htmlFor="savings" className="form-label">
                      {t('dashboard.tourPage.savings') || 'Economie (€)'}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="savings"
                      name="savings"
                      value={formData.savings}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="col-md-12 mb-4">
                    <div className="tour-info">
                      <p>
                        <strong>{t('dashboard.tourPage.slug') || 'Slug'}:</strong>{' '}
                        <code>{selectedTour.slug}</code>
                      </p>
                      <p>
                        <strong>{t('dashboard.tourPage.addedBy') || 'Créé par'}:</strong>{' '}
                        {selectedTour.addedBy.username}
                      </p>
                      {selectedTour.createdAt && (
                        <p>
                          <strong>
                            {t('dashboard.tourPage.createdAt') || 'Date de création'}:
                          </strong>{' '}
                          {new Date(selectedTour.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      <p>
                        <strong>Nombre d'étapes:</strong> {selectedTour.steps?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {t('dashboard.tourPage.updateButton') || 'Mettre à jour'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger ms-2"
                    onClick={handleDelete}
                  >
                    {t('dashboard.tourPage.deleteButton') || 'Supprimer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Gestion des étapes */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title mb-0">
                Étapes du parcours ({selectedTour.steps?.length || 0})
              </h3>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleOpenStepModal()}
              >
                + Ajouter une étape
              </button>
            </div>
            <div className="card-body p-0">
              {selectedTour.steps && selectedTour.steps.length > 0 ? (
                <div className="steps-list">
                  {selectedTour.steps
                    .sort((a, b) => a.order - b.order)
                    .map((step, index) => (
                      <div key={step.order} className="step-item">
                        <div className="step-header">
                          <div className="step-number">{step.order}</div>
                          <div className="step-content">
                            <h5 className="step-title">{step.title}</h5>
                            <p className="step-description">{step.description}</p>
                            <div className="step-meta">
                              {step.location?.address && (
                                <span className="step-badge">
                                  📍 {step.location.address}
                                </span>
                              )}
                              {step.duration && (
                                <span className="step-badge">
                                  ⏱️ {step.duration} min
                                </span>
                              )}
                              {(step.teaserPostId || step.fullPostId) && (
                                <button
                                  className="step-badge video-badge clickable"
                                  onClick={() => toggleVideo(step.order)}
                                >
                                  {step.teaserPostId && step.fullPostId ? (
                                    <>🎬 {expandedVideos.has(step.order) ? 'Masquer les posts' : 'Voir les posts'}</>
                                  ) : (
                                    <>🎥 {expandedVideos.has(step.order) ? 'Masquer le post' : 'Voir le post'}</>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="step-actions">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenStepModal(step)}
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => handleDeleteStep(step.order)}
                          >
                            🗑️ Supprimer
                          </button>
                        </div>

                        {/* Affichage des posts/vidéos si expanded */}
                        {expandedVideos.has(step.order) && (
                          <div className="step-video-container">
                            {(step.teaserVideoUrl || (step.teaserPostId && typeof step.teaserPostId === 'object')) && (
                              <div className="video-section mb-3">
                                <div className="video-header">
                                  <h6>
                                    <i className="fas fa-play-circle me-2 text-info"></i>
                                    Post Teasing (gratuit)
                                    {step.teaserPostId && typeof step.teaserPostId === 'object'
                                      ? ` - ${step.teaserPostId.name}`
                                      : ''}
                                  </h6>
                                  <span className="badge bg-info">ShortVideo</span>
                                </div>
                                {(step.teaserVideoUrl ||
                                  (step.teaserPostId &&
                                    typeof step.teaserPostId === 'object' &&
                                    step.teaserPostId.shortVideo?.previewUrl)) && (
                                  <VideoPlayer
                                    url={
                                      step.teaserVideoUrl ||
                                      (step.teaserPostId as any).shortVideo.previewUrl
                                    }
                                    title={`${step.title} - Teasing`}
                                    height="300px"
                                  />
                                )}
                              </div>
                            )}

                            {(step.fullVideoUrl || (step.fullPostId && typeof step.fullPostId === 'object')) && (
                              <div className="video-section">
                                <div className="video-header">
                                  <h6>
                                    <i className="fas fa-video me-2 text-primary"></i>
                                    Post Complet (premium)
                                    {step.fullPostId && typeof step.fullPostId === 'object'
                                      ? ` - ${step.fullPostId.name}`
                                      : ''}
                                  </h6>
                                  <span className="badge bg-primary">MainVideo</span>
                                </div>
                                {(step.fullVideoUrl ||
                                  (step.fullPostId &&
                                    typeof step.fullPostId === 'object' &&
                                    step.fullPostId.mainVideo?.previewUrl)) && (
                                  <VideoPlayer
                                    url={
                                      step.fullVideoUrl ||
                                      (step.fullPostId as any).mainVideo.previewUrl
                                    }
                                    title={`${step.title} - Complet`}
                                    height="350px"
                                  />
                                )}
                              </div>
                            )}

                            {/* Affichage simple si les posts ne sont pas populés */}
                            {step.teaserPostId && typeof step.teaserPostId === 'string' && (
                              <div className="alert alert-info">
                                <i className="fas fa-info-circle me-2"></i>
                                Post teasing ID: {step.teaserPostId}
                              </div>
                            )}
                            {step.fullPostId && typeof step.fullPostId === 'string' && (
                              <div className="alert alert-info">
                                <i className="fas fa-info-circle me-2"></i>
                                Post complet ID: {step.fullPostId}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="p-4 text-center text-muted">
                  <p>Aucune étape pour ce parcours.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleOpenStepModal()}
                  >
                    Ajouter la première étape
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Carte des étapes */}
      {selectedTour.steps && selectedTour.steps.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <i className="fas fa-map-marked-alt me-2"></i>
                  Carte du parcours
                </h3>
              </div>
              <div className="card-body">
                <TourMap steps={selectedTour.steps} height="400px" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour ajouter/modifier une étape - Rendu via Portal */}
      {isStepModalOpen && ReactDOM.createPortal(
        <div
          className="ream-modal show"
          style={{ display: 'block' }}
          onClick={handleCloseStepModal}
        >
          <div
            className="ream-modal-dialog ream-modal-centered ream-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ream-modal-content">
              <div className="ream-modal-header">
                <h5 className="modal-title">
                  {editingStep ? 'Modifier l\'étape' : 'Ajouter une étape'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseStepModal}
                  aria-label="Close"
                />
              </div>
              <form onSubmit={handleSubmitStep}>
                <div className="ream-modal-body">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-title" className="form-label">
                        Titre de l'étape * (FR)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="step-title"
                        name="title"
                        value={stepFormData.title}
                        onChange={handleStepInputChange}
                        required
                        placeholder="Ex: Tour Eiffel, Musée du Louvre..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-title-en" className="form-label">
                        Titre de l'étape (EN)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="step-title-en"
                        name="title_en"
                        value={stepFormData.title_en || ''}
                        onChange={handleStepInputChange}
                        placeholder="Ex: Eiffel Tower, Louvre Museum..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-description" className="form-label">
                        Description * (FR)
                      </label>
                      <textarea
                        className="form-control"
                        id="step-description"
                        name="description"
                        value={stepFormData.description}
                        onChange={handleStepInputChange}
                        rows={3}
                        required
                        placeholder="Décrivez cette étape du parcours..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-description-en" className="form-label">
                        Description (EN)
                      </label>
                      <textarea
                        className="form-control"
                        id="step-description-en"
                        name="description_en"
                        value={stepFormData.description_en || ''}
                        onChange={handleStepInputChange}
                        rows={3}
                        placeholder="Describe this tour step..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Localisation
                      </label>
                      <LocationPicker
                        onLocationSelect={handleLocationSelect}
                        initialLocation={stepFormData.location}
                        placeholder="Rechercher l'adresse de cette étape..."
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="step-duration" className="form-label">
                        Durée estimée (minutes)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="step-duration"
                        name="duration"
                        value={stepFormData.duration || 0}
                        onChange={handleStepInputChange}
                        min="0"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <PostSelector
                        value={(stepFormData.fullPostId || stepFormData.teaserPostId) as string}
                        onChange={(postId, post) => {
                          handleStepInputChange({
                            target: {
                              name: 'teaserPostId',
                              value: postId || '',
                            },
                          } as any);

                          handleStepInputChange({
                            target: {
                              name: 'fullPostId',
                              value: postId || '',
                            },
                          } as any);

                          handleStepInputChange({
                            target: {
                              name: 'teaserVideoUrl',
                              value: post?.shortVideo?.previewUrl || '',
                            },
                          } as any);

                          handleStepInputChange({
                            target: {
                              name: 'fullVideoUrl',
                              value: post?.mainVideo?.previewUrl || '',
                            },
                          } as any);
                        }}
                        label={
                          <>
                            <i className="fas fa-video me-2"></i>
                            Vidéo (preview si non payé, complète si payé)
                          </>
                        }
                        placeholder="Sélectionner un post vidéo..."
                        videoType="both"
                      />
                      <small className="form-text text-muted">
                        🎁 Le `shortVideo.previewUrl` sera utilisé en preview (non payé)
                        <br />
                        💎 Le `mainVideo.previewUrl` sera utilisé en version complète (payé)
                      </small>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-tips" className="form-label">
                        Conseils (FR)
                      </label>
                      <textarea
                        className="form-control"
                        id="step-tips"
                        name="tips"
                        value={stepFormData.tips || ''}
                        onChange={handleStepInputChange}
                        rows={2}
                        placeholder="Conseils pratiques pour cette étape..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="step-tips-en" className="form-label">
                        Conseils (EN)
                      </label>
                      <textarea
                        className="form-control"
                        id="step-tips-en"
                        name="tips_en"
                        value={stepFormData.tips_en || ''}
                        onChange={handleStepInputChange}
                        rows={2}
                        placeholder="Practical tips for this step..."
                      />
                    </div>
                  </div>
                </div>
                <div className="ream-modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseStepModal}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingStep ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default TourDetails;
